"use client";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PendingSignsAccordion from "../tbs-sender/components/PendingSignsAccordion";
import ReturnedExtraItemsList from "../tbs-sender/components/returned-extra-items-list";
import { useParams } from "react-router-dom";
import Loader from "../../../components/loader";
import { APIGetTBSDetails } from "../../../service/api-service";
import { GetTBSDetailsResEntity } from "../../../interface/tbs-tracker/send-tbs";
import { handleDownloadFilesFromLinks } from "../../../service/shared.service";
import { toastOptObj } from "../../../utils/constants";
import { toast } from "react-toastify";
import { IsNullOrUndefined } from "../../../utils/null-check";
import { Image } from "primereact/image";

/** Zoom / Packing picture as a thumbnail (click it to see it full size) with its file name; the download stays next to the label. */
const PictureThumbnail = ({ label, url }: { label: string; url?: string }) => (
    <div className="col-lg-3 col-md-6">
        <div className="fw-medium mb-1 d-flex align-items-center gap-2">
            {label}
            {url && (
                <i
                    className="pi pi-download"
                    title={`Download ${label}`}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleDownloadFilesFromLinks([url])}
                />
            )}
        </div>
        {url ? (
            <div className="file-card d-flex flex-column align-items-center justify-content-between">
                <div className="d-flex align-items-center justify-content-center flex-grow-1 file-card-preview">
                    <Image src={url} zoomSrc={url} preview width="130" height="100" imageClassName="file-card-image" alt={label} />
                </div>
                <div className="file-card-footer text-center text-truncate w-100" title={url.split("/").pop()}>
                    {url.split("/").pop()}
                </div>
            </div>
        ) : (
            <small className="text-muted">-</small>
        )}
    </div>
);

export default function ViewTBSItem() {
    const navigate = useNavigate()
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [viewData, setViewData] = useState<GetTBSDetailsResEntity | null>(null);
    const fetchTBSDetails = async () => {
        if (!id) return;
        setIsLoading(true);
        try {
            const resp = await APIGetTBSDetails(id);
            if (
                resp &&
                resp.data &&
                (resp.status === 200)
            ) {
                const apiResp = resp.data;
                if (apiResp && apiResp.statusCode === 200 && apiResp.data) {
                    setViewData(apiResp.data);
                } else {
                    toast.error(apiResp.message || apiResp.errorMsg, {
                        ...toastOptObj,
                    });
                    navigate(-1);
                    return;
                }
            } else {
                toast.error(resp?.data?.message || "Unable to fetch TBS details.", {
                    ...toastOptObj,
                });
                navigate(-1);
                return;
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!id) {
            navigate(-1);
            return;
        }

        fetchTBSDetails();
    }, [id]);

    if (isLoading || !viewData) {
        return <Loader isLoading={true} />;
    }
    return (
        <div className="content-wrapper">

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="mb-0 font-bold">
                    View TBS Item
                </h2>
            </div>
            <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body p-4">
                    <div className="row g-4">
                        <div className="col-md-3">
                            <div className="fw-medium mb-1">
                                TBS Number
                            </div>
                            <small className="text-muted">
                                {viewData.tbsNumber || "-"}
                            </small>
                        </div>

                        <div className="col-md-3">
                            <div className="fw-medium mb-1">
                                Date
                            </div>
                            <small className="text-muted">
                                {viewData.tbsDate}
                            </small>
                        </div>
                    </div>
                </div>
            </div>
            <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body p-4">

                    <h4 className="card-title mb-4">
                        Model Details
                    </h4>

                    <div className="row g-4">

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">Category</div>
                            <small className="text-muted">
                                {viewData.model.categoryName}
                            </small>
                        </div>

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">Branded Type</div>
                            <small className="text-muted">
                                {viewData.model.isBrand ? "Branded" : "Non-Branded"}
                            </small>
                        </div>

                        {viewData.model.isBrand && (
                            <>
                                <div className="col-lg-3 col-md-6">
                                    <div className="fw-medium mb-1">Brand Name</div>
                                    <small className="text-muted">
                                        {viewData.model.brandName || "-"}
                                    </small>
                                </div>

                                <div className="col-lg-3 col-md-6">
                                    <div className="fw-medium mb-1">Brand Code</div>
                                    <small className="text-muted">
                                        {viewData.model.brandCode || "-"}
                                    </small>
                                </div>
                            </>
                        )}
                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">Thickness</div>
                            <small className="text-muted">
                                {!IsNullOrUndefined(viewData.model.thickness)
                                    ? `${viewData.model.thickness} MM`
                                    : "-"}
                            </small>
                        </div>

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">QTY</div>
                            <small className="text-muted">
                                {viewData.model.qty}
                            </small>
                        </div>

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">Width</div>
                            <small className="text-muted">
                                {!IsNullOrUndefined(viewData.model.width)
                                    ? `${viewData.model.width} MM`
                                    : "-"}
                            </small>
                        </div>

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">Height</div>
                            <small className="text-muted">
                                {!IsNullOrUndefined(viewData.model.height)
                                    ? `${viewData.model.height} MM`
                                    : "-"}
                            </small>
                        </div>

                    </div>

                </div>
            </div>
            <div className="card border-0 shadow-sm rounded-4 mb-4">
                <div className="card-body p-4">

                    <h4 className="card-title mb-4">
                        Dispatch Details
                    </h4>

                    <div className="row g-4">

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">
                                Select Size
                            </div>
                            <small className="text-muted">
                                {viewData.dispatch.selectSize}
                            </small>
                        </div>

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">
                                Drawing Followed
                            </div>
                            <small className="text-muted">
                                {viewData.dispatch.anyDrawing ? "Yes" : "No"}
                            </small>
                        </div>

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">
                                Off Cut Required
                            </div>
                            <small className="text-muted">
                                {viewData.dispatch.offCutRequired ? "Yes" : "No"}
                            </small>
                        </div>

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">
                                Dispatch From
                            </div>
                            <small className="text-muted">
                                {viewData.dispatch.dispatchFrom}
                            </small>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">
                                Mode of Transport
                            </div>
                            <small className="text-muted">
                                {viewData.dispatch.modeOfTransport}
                            </small>
                        </div>

                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">
                                Concern Person Name
                            </div>
                            <small className="text-muted">
                                {viewData.dispatch.concernPersonName}
                            </small>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">
                                Contact Number
                            </div>
                            <small className="text-muted">
                                {viewData.dispatch.contactNumber}
                            </small>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">Sender Remarks</div>
                            <small className="text-muted d-block">
                                {viewData.dispatch.senderRemarks}
                            </small>
                        </div>
                        {viewData.dispatch.drawingURL &&
                            <div className="col-lg-3 col-md-6">
                                <div className="fw-medium mb-1">
                                    DRG File
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <small className="text-muted text-truncate">
                                        {viewData.dispatch.drawingURL
                                            ? viewData.dispatch.drawingURL.split("/").pop()
                                            : "-"}
                                    </small>
                                    <i
                                        className="pi pi-download"
                                        style={{ cursor: "pointer" }}
                                        onClick={() =>
                                            viewData.dispatch.drawingURL &&
                                            handleDownloadFilesFromLinks([viewData.dispatch.drawingURL])
                                        }
                                    />
                                </div>
                            </div>}
                        <div className="col-lg-3 col-md-6">
                            <div className="fw-medium mb-1">
                                Dispatch File
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <small className="text-muted text-truncate">
                                    {viewData.dispatch.dispatchDocumentURL
                                        ? viewData.dispatch.dispatchDocumentURL.split("/").pop()
                                        : "-"}
                                </small>
                                <i
                                    className="pi pi-download"
                                    style={{ cursor: "pointer" }}
                                    onClick={() =>
                                        viewData.dispatch.dispatchDocumentURL &&
                                        handleDownloadFilesFromLinks([viewData.dispatch.dispatchDocumentURL])
                                    }
                                />
                            </div>
                        </div>
                        <PictureThumbnail label="Zoom Picture" url={viewData.dispatch.zoomPicture} />
                        <PictureThumbnail label="Packing Picture" url={viewData.dispatch.packingPicture} />

                    </div>
                </div>
            </div>
            {viewData.returnedItems && viewData.returnedItems.length > 0 && (
                <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4">
                        <h4 className="card-title mb-2">
                            Returned Items
                        </h4>
                        <small className="text-muted d-block mb-4">
                            The receiver did not accept these items. They are no longer part of this TBS and are pending for a new TBS.
                        </small>
                        <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>ORDER NO.</th>
                                        <th>PROJECT</th>
                                        <th>LOCATION NO.</th>
                                        <th>RETURNED ITEMS</th>
                                        <th>RETURNED BY</th>
                                        <th>RETURNED ON</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewData.returnedItems.map((item) => (
                                        <tr key={item._id}>
                                            <td>{item.orderNumber || "-"}</td>
                                            <td>{item.projectName || "-"}</td>
                                            <td>{item.solutionNo || "-"}</td>
                                            <td>{item.items.join(", ") || "-"}</td>
                                            <td>{item.returnedBy || "-"}</td>
                                            <td>{item.returnedAt || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            {viewData.handleFabricationCodes && viewData.handleFabricationCodes.length > 0 && (
                <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4">
                        <h4 className="card-title mb-4">
                            Handle Fabrication Codes
                        </h4>
                        <div className="table-responsive">
                            <table className="table table-sm align-middle mb-0">
                                <thead>
                                    <tr>
                                        <th>ORDER NO.</th>
                                        <th>PROJECT</th>
                                        <th>LOCATION NO.</th>
                                        <th>FABRICATION CODE</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {viewData.handleFabricationCodes.map((handle) => (
                                        <tr key={handle.locationId}>
                                            <td>{handle.orderNumber || "-"}</td>
                                            <td>{handle.projectName || "-"}</td>
                                            <td>{handle.solutionNo || "-"}</td>
                                            <td>{handle.fabricationCode || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            {viewData.extraItemReturns && viewData.extraItemReturns.length > 0 && (
                <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4">
                        <h4 className="card-title mb-4">
                            Returned Extra Items
                        </h4>
                        <ReturnedExtraItemsList returns={viewData.extraItemReturns} />
                    </div>
                </div>
            )}
            {viewData.locations.map((order) => (
                <PendingSignsAccordion
                    key={order.orderDetails.orderId}
                    title={`${order.orderDetails.orderNumber} - ${order.orderDetails.projectName}`}
                    locations={order.allSolutionList.filter(
                        (item) =>
                            item.inside ||
                            item.outside ||
                            item.handle
                    ).map((item) => ({
                        id: item.solutionId,
                        locationNo: item.solutionNo.toString(),
                        category: item.category,
                        system: {
                            wall: item.systemsArr[0],
                            flush: item.systemsArr[1],
                            fireRated: item.systemsArr[2],
                            direction: item.systemsArr[3],
                            code: item.csCode,
                            dropSeal: item.systemsArr[5] ?? "",
                            handle: item.systemsArr[6] ?? "",
                            doorCloser: item.systemsArr[7] ?? "",
                        },
                        specs: {
                            location: item.floor,
                            size: item.specsArr[2],
                            fireRating: item.specsArr[3],
                            frame: item.specsArr[4] ?? "",
                            glassOut: item.specsArr[5] ?? "",
                            glassIn: item.specsArr[6] ?? "",
                        },
                        inside: item.inside,
                        outside: item.outside,
                        handle: item.handle,
                        showInside: item.TBSglassOneIn,
                        showOutside: item.TBSglassTwoOut,
                        showHandle: item.TBSHandle,
                    }))}
                    isReview
                />
            ))}
            <div className="row mt-4">
                <div className="col-md-12 d-flex align-items-center justify-content-between">
                    <button
                        type="button"
                        className="btn btn-primary btn-md font-weight-medium m-0 me-2"
                        onClick={() => { navigate(-1) }}
                    >
                        Back
                    </button>
                </div>
            </div>

        </div >
    );
}